import http from "@/shared/services/http.config";

export type StaffPayload = {
  id?: number | string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  assigned_roles: Array<number | string>;
};

export const updateHolidayMode = (payload: Record<string, any>) =>
  http.post("/vendors/holiday-mode", payload).then((res) => res.data);

export const getHolidayModeStatus = () =>
  http.get("/vendors/holiday-mode").then((res) => res.data);

// Services
export const getVendorServiceList = () =>
  http.get("/vendors/service-list").then((res) => res.data);

export const getVendorServiceDetail = (id: string | number) =>
  http.get(`/vendors/services/${id}/detail`).then((res) => res.data);

export const deleteService = (id: string | number) =>
  http.delete(`/vendors/services/${id}/delete`).then((res) => res.data);

// Service packages
export const getVendorServicePackageList = ({ is_vendor }: { is_vendor: string }) =>
  http.get("/vendors/service-packages", { params: { is_vendor } }).then((res) => res.data);

export const getVendorServicePackageDetail = (id: string | number) =>
  http.get(`/vendors/service-packages/${id}/detail`).then((res) => res.data);

export const deleteServicePackage = (id: string | number) =>
  http.delete(`/vendors/service-packages/${id}/delete`).then((res) => res.data);

// Payment credentials
export const getVendorPaymentCredentialList = () =>
  http.get("/vendors/payment-credentials").then((res) => res.data);

export const getVendorPaymentCredentialDetail = (id: string | number) =>
  http.get(`/vendors/payment-credentials/${id}/detail`).then((res) => res.data);

export const storeVendorPaymentCredential = (payload: Record<string, any>) =>
  http.post("/vendors/payment-credentials", payload).then((res) => res.data);

// Save service (create or update)
export const saveService = (payload: Record<string, any>) =>
  http.post("/vendors/service-save", payload).then((res) => res.data);

// Save service package (create or update)
// NOTE: endpoint name follows provided server route ("servie-package-save")
export const saveServicePackage = (payload: Record<string, any>) =>
  http.post("/vendors/service-package-save", payload).then((res) => res.data);

export const connectPackageToStripe = (id: string | number) =>
  http.post(`/vendors/service-packages/${id}/connect-stripe`).then((res) => res.data);

export const fetchServiceProviders = (vendorId: string | number, params: Record<string, any> = {}) =>
  http.get(`/vendors/${vendorId}/service-providers`, { params }).then((res) => res.data);

export const fetchVendorUsers = (params: Record<string, any> = {}) =>
  http.get(`/vendors/staffs`, { params }).then((res) => res.data);

export const fetchCustomers = (params: Record<string, any> = {}) =>
  http.get("/vendors/customer-list", { params }).then((res) => res.data);

// ---- Vendor related endpoints (migrated from shared/services/vendors.api.ts) ----

export const fetchVendors = (params: Record<string, any> = {}) =>
  http.get("/vendors", { params }).then((res) => res.data);

export const fetchStaffs = (params: Record<string, any> = {}) =>
  http.get("/vendors/staffs", { params }).then((res) => res.data);

export const fetchStaffDetails = (
  staffId: string | number,
  params: Record<string, any> = {}
) => http.get(`/vendors/staffs/${staffId}/details`, { params }).then((res) => res.data);

export const nearByVendors = (params: Record<string, any> = {}) =>
  http.get("/vendors/nearby", { params }).then((res) => res.data);

export const fetchVendorTypes = (params: Record<string, any> = {}) =>
  http.get("/vendors/types", { params }).then((res) => res.data);

export const fetchVendorById = (id: string | number, params: Record<string, any> = {}) =>
  http.get(`/vendors/${id}/details`, { params }).then((res) => res.data);

export const fetchVendorByCode = (code: string, params: Record<string, any> = {}) =>
  http.get(`/vendors/${code}/by-code`, { params }).then((res) => res.data);

export const fetchVendorServiceHierarchy = (code: string, params: Record<string, any> = {}) =>
  http.get(`/vendors/${code}/service-hierarchy`, { params }).then((res) => res.data);

export const fetchVendorAvailableDates = (code: string, params: Record<string, any> = {}) =>
  http.get(`/vendors/${code}/available-dates`, { params }).then((res) => res.data.data);

export const fetchVendorOffers = (code: string, params: Record<string, any> = {}) =>
  http.get(`/vendors/${code}/offers`, { params }).then((res) => res.data);

export const fetchVendorSchedule = (code: string, params: Record<string, any> = {}) =>
  http.get(`/vendors/${code}/schedules`, { params }).then((res) => res.data);

export const fetchVendorServices = (vendorId: string | number, params: Record<string, any> = {}) =>
  http.get(`/vendors/${vendorId}/services`, { params }).then((res) => res.data);

export const handleSubmitStaff = (payload: StaffPayload) =>
  http.post(`/vendors/store-user`, payload).then((res) => res.data);

export const fetchVendorDashboardSummary = (params: Record<string, any> = {}) =>
  http.get("/vendor/dashboard/summary", { params }).then((res) => res.data.summary);

export const fetchVendorDefaultPaymentCredential = (
  vendor_code: string,
  params: Record<string, any> = {},
) => http.get(`/vendors/${vendor_code}/payment-credential`, { params }).then((res) => res.data);

export const fetchVendorQuestions = (params: Record<string, any> = {}) =>
  http.get("/vendors/questions", { params }).then((res) => res.data);

export const fetchQuestionDetail = (id: string | number, params: Record<string, any> = {}) =>
  http.get(`/vendors/questions/${id}/details`, { params }).then((res) => res.data);

export const saveQuestion = (payload: Record<string, any>) =>
  http.post("/vendors/question-save", payload).then((res) => res.data);

export const deleteQuestion = (id: string | number) =>
  http.delete(`/vendors/questions/${id}/delete`).then((res) => res.data);

export const fetchCompanies = (params = {}) =>
  http.get('/vendors/companies', { params }).then((res) => res.data)

export const getCompanyDetails = (id: string | number, params: Record<string, any> = {}) =>
  http.get(`/vendors/companies/${id}/details`, { params }).then((res) => res.data)

// Backwards/alternate naming used by some pages.
export const fetchCompanyDetails = (id: string | number, params: Record<string, any> = {}) =>
  http.get(`/vendors/companies/${id}/details`, { params }).then((res) => res.data)
